import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { SettingsBrandingResponse } from '@verybigthings/trackframe-shared/schemas';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { hubUrl } from '@verybigthings/trackframe-hub-client';
import { hub, hubBaseUrl } from '@/lib/hub-client';
import { createLogoSender, type LogoVariant } from '@/lib/logo-request';
import { FEELS, useTheme, type Feel, type Mode } from '@/lib/theme';
import { cn } from '@/lib/utils';
import { ConfigurationSection } from './ConfigurationSection';

const ACCEPT = 'image/png,image/jpeg,image/svg+xml,image/webp';

// Singleton settings page: the company logo (light + an optional dark-mode
// variant, HUB-125 D7) rendered on the shared `<TrackframeHeader />`, the
// per-browser theme picker, and the hub settings registry. Uploads use raw
// fetch instead of the typed `HubClient` because the multipart endpoints
// (`PUT /settings/logo`, `PUT /settings/logo/dark`) aren't a JSON shape the
// typed client surfaces; a one-off fetch is cleaner than wedging multipart
// support into the client just for this.
export function Settings() {
  const [branding, setBranding] = useState<SettingsBrandingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    hub
      .settingsBranding()
      .then(setBranding)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Hub-wide configuration shared across the admin, the hub root, and every track.
        </p>
      </div>

      <LogoCard
        variant="light"
        title="Company logo"
        description="Shown on the shared header. PNG, JPEG, SVG, or WebP — up to 1 MB."
        url={branding?.logoUrl ?? null}
        loading={loading}
        loadError={error}
        onChange={setBranding}
      />
      <LogoCard
        variant="dark"
        title="Logo for dark mode"
        description="Optional. Shown instead of the company logo when the page is in dark mode. Without it the company logo is used in both modes."
        url={branding?.logoDarkUrl ?? null}
        loading={loading}
        loadError={error}
        onChange={setBranding}
      />

      <AppearanceCard />

      <ConfigurationSection />
    </div>
  );
}

// One upload card per logo variant. The hub answers every PUT with the full
// branding descriptor, so a successful upload refreshes both cards at once;
// a DELETE answers 204 and the card clears its own URL locally.
export function LogoCard({
  variant,
  title,
  description,
  url,
  loading,
  loadError,
  onChange,
}: {
  variant: LogoVariant;
  title: string;
  description: string;
  url: string | null;
  loading: boolean;
  loadError: string | null;
  onChange: (next: SettingsBrandingResponse | ((prev: SettingsBrandingResponse | null) => SettingsBrandingResponse | null)) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Raw multipart/DELETE sender that still rides the client's silent session
  // refresh (HUB-104) — see logo-request.ts.
  const send = useMemo(() => createLogoSender(hubBaseUrl, variant), [variant]);
  const urlKey = variant === 'dark' ? 'logoDarkUrl' : 'logoUrl';

  const onUpload = async (file: File) => {
    setSubmitting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await send('PUT', fd);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `${res.status} ${res.statusText}`);
      }
      onChange((await res.json()) as SettingsBrandingResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onRemove = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await send('DELETE');
      if (!res.ok && res.status !== 204) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `${res.status} ${res.statusText}`);
      }
      onChange((prev) => (prev ? { ...prev, [urlKey]: null } : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <>
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <div
                className={cn(
                  'flex h-16 w-full items-center justify-center rounded-md border sm:w-40',
                  variant === 'dark' ? 'bg-neutral-900' : 'bg-muted/30',
                )}
              >
                {url ? (
                  <img src={hubUrl(hubBaseUrl, url)} alt="Current logo" className="max-h-12 w-auto" />
                ) : (
                  <span className="text-xs text-muted-foreground">No logo uploaded</span>
                )}
              </div>
              <div className="flex w-full gap-2 sm:w-auto">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT}
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void onUpload(file);
                    // Reset so re-selecting the same file fires onChange again.
                    e.target.value = '';
                  }}
                />
                <Button type="button" className="flex-1 sm:flex-none" onClick={() => fileInputRef.current?.click()} disabled={submitting}>
                  {submitting ? 'Uploading…' : url ? 'Replace logo' : 'Upload logo'}
                </Button>
                {url && (
                  <Button type="button" variant="outline" className="flex-1 sm:flex-none" onClick={onRemove} disabled={submitting}>
                    Remove
                  </Button>
                )}
              </div>
            </div>
            {(error ?? loadError) && <p className="text-sm text-destructive">{error ?? loadError}</p>}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Theme picker for the three axes (feel / mode / palette) managed by
// src/lib/theme.tsx. Per-browser preference, not a hub-wide setting.
function AppearanceCard() {
  const { feel, mode, palette, setFeel, setMode, setPalette } = useTheme();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Appearance</CardTitle>
        <CardDescription>How the admin looks for you. Saved in this browser.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-0 divide-y">
        <AppearanceRow label="Feel" hint="Overall visual direction">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {(Object.keys(FEELS) as Feel[]).map((key) => (
              <button
                key={key}
                type="button"
                aria-pressed={feel === key}
                onClick={() => setFeel(key)}
                className={cn(
                  'cursor-pointer rounded-lg border bg-card p-3 text-left transition-colors hover:bg-muted/50',
                  feel === key && 'border-ring ring-1 ring-ring',
                )}
              >
                <span className="mb-2 flex gap-1">
                  {FEELS[key].dots.map((dot, i) => (
                    <span
                      key={i}
                      className="size-3 rounded-full border border-black/10"
                      style={{ background: dot }}
                    />
                  ))}
                </span>
                <span className="block text-sm font-semibold">{FEELS[key].label}</span>
                <span className="block text-xs text-muted-foreground">{FEELS[key].blurb}</span>
              </button>
            ))}
          </div>
        </AppearanceRow>

        <AppearanceRow label="Mode" hint="Light or dark">
          <div className="inline-flex w-fit rounded-[var(--radius-ctl)] border bg-muted p-0.5">
            {(['light', 'dark'] as Mode[]).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={mode === m}
                onClick={() => setMode(m)}
                className={cn(
                  'cursor-pointer rounded-[calc(var(--radius-ctl)-2px)] px-4 py-1.5 text-xs font-semibold capitalize text-muted-foreground',
                  mode === m && 'bg-card text-foreground shadow-sm',
                )}
              >
                {m}
              </button>
            ))}
          </div>
        </AppearanceRow>

        <AppearanceRow label="Palette" hint="Accent color within the feel">
          <div className="flex flex-wrap gap-2">
            {FEELS[feel].palettes.map((p) => (
              <button
                key={p.id}
                type="button"
                aria-pressed={palette === p.id}
                onClick={() => setPalette(p.id)}
                className={cn(
                  'inline-flex cursor-pointer items-center gap-2 rounded-[var(--radius-ctl)] border bg-card py-1.5 pl-2 pr-3 text-xs font-semibold transition-colors hover:bg-muted/50',
                  palette === p.id && 'border-ring ring-1 ring-ring',
                )}
              >
                <span className="size-3.5 rounded-full" style={{ background: p.swatch }} />
                {p.label}
              </button>
            ))}
          </div>
        </AppearanceRow>
      </CardContent>
    </Card>
  );
}

function AppearanceRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint: string;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-3 py-4 first:pt-0 last:pb-0 sm:grid-cols-[180px_1fr] sm:gap-6">
      <div>
        <div className="text-sm font-semibold">{label}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
      <div>{children}</div>
    </div>
  );
}
